import { Box, Card as MuiCard, Typography } from '@mui/material';
import type { LucideIcon } from 'lucide-react';
import { useId, type ReactElement, type ReactNode } from 'react';

import { Badge, type BadgeTone } from './Badge';
import { displayValue, type DisplayableValue } from './displayValue';
import { Icon } from './Icon';

/**
 * Indicateur chiffre (maquette « Indicateur » : libelle, grande valeur,
 * variation, phrase, courbe).
 *
 * Trois regles le distinguent d'une simple carte, et sont la raison de son
 * existence :
 *
 * - **la valeur est fournie par l'appelant**, telle que le serveur l'a rendue ;
 *   absente, elle s'affiche « — », jamais `0` ni un nombre deduit d'une page ;
 * - **la variation n'est affichee que si elle est fournie**, avec un libelle
 *   ecrit (« +6,2 % ») : le ton de la pastille ne dit pas seul si c'est bien ;
 * - **la courbe n'est rendue que si l'appelant la fournit** (`chart`). Le kit ne
 *   dessine aucun graphique (ils viennent du lot des graphiques SVG) et ne
 *   reserve pas de place vide : pas de serie, pas de courbe.
 *
 * L'indicateur est une region nommee par son libelle.
 */
export interface MetricProps {
  readonly label: string;
  readonly value: DisplayableValue;
  readonly icon?: LucideIcon;
  /** Phrase sous la valeur : unite, periode, source, ou pourquoi elle manque. */
  readonly caption?: ReactNode;
  readonly delta?: { readonly label: string; readonly tone: BadgeTone };
  /** Courbe d'evolution, fournie seulement quand une serie existe. */
  readonly chart?: ReactNode;
  /** Pictogramme d'etat a cote de la valeur (sonde disponible ou non). */
  readonly status?: ReactNode;
}

export const Metric = ({ label, value, icon, caption, delta, chart, status }: MetricProps): ReactElement => {
  const labelId = useId();

  return (
    <MuiCard
      component="section"
      aria-labelledby={labelId}
      sx={{ p: 2.25, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, flex: '1 1 240px' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'tokens.textMuted' }}>
        {icon === undefined ? null : <Icon icon={icon} size={16} />}
        <Typography id={labelId} component="h2" sx={{ fontSize: 13, fontWeight: 600, color: 'tokens.textMuted' }}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {status}
        <Typography component="p" sx={{ fontSize: 30, fontWeight: 800, lineHeight: '36px', flex: 1, minWidth: 0 }}>
          {displayValue(value)}
        </Typography>
        {chart}
      </Box>
      {delta === undefined && caption === undefined ? null : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          {delta === undefined ? null : <Badge label={delta.label} tone={delta.tone} />}
          {caption === undefined ? null : (
            <Typography component="div" sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
              {caption}
            </Typography>
          )}
        </Box>
      )}
    </MuiCard>
  );
};
